import React, { useRef, useState, useCallback } from 'react';

type Props = {
  id: string;
  title: string;
  subtitle?: React.ReactNode;
  defaultOpen?: boolean;
  /**
   * When true (default for sections that start collapsed), the closed state looks tappable:
   * dashed border, light fill, and an “Expand” label next to the chevron.
   */
  emphasizeExpandable?: boolean;
  /** Runs once, the first time the user expands this block (for lazy data loading). */
  onFirstExpand?: () => void;
  /** e.g. status pill aligned with the title row */
  headerEnd?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
};

/**
 * Accordion block for the Reports page: all sections can start collapsed.
 * Expanding mounts the body; use onFirstExpand to load heavy data.
 */
const CollapsibleReportSection: React.FC<Props> = ({
  id,
  title,
  subtitle,
  defaultOpen = false,
  emphasizeExpandable = false,
  onFirstExpand,
  headerEnd,
  children,
  className = '',
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const didCallFirstExpand = useRef(false);

  const handleToggle = useCallback(() => {
    setOpen((prev) => {
      if (!prev) {
        if (onFirstExpand && !didCallFirstExpand.current) {
          didCallFirstExpand.current = true;
          onFirstExpand();
        }
        return true;
      }
      return false;
    });
  }, [onFirstExpand]);

  const showHint = emphasizeExpandable && !open;

  return (
    <div
      className={`bg-white rounded-lg shadow transition-[border,box-shadow] ${
        showHint
          ? 'border-2 border-dashed border-slate-300/90 ring-1 ring-slate-200/60'
          : 'border border-gray-100/80'
      } ${className}`.trim()}
    >
      <div className="flex items-start gap-2 pr-1">
        <button
          type="button"
          onClick={handleToggle}
          className={`group flex-1 min-w-0 flex items-start justify-between gap-3 px-4 py-3 text-left transition rounded-tl-lg ${
            showHint ? 'bg-slate-50/95 hover:bg-slate-100/90' : 'hover:bg-gray-50/80'
          }`}
          aria-expanded={open}
          aria-controls={`${id}-panel`}
          id={`${id}-header`}
          aria-label={open ? `Collapse ${title}` : `Expand ${title}`}
        >
          <div className="min-w-0 pr-2">
            <h2 className="text-lg font-semibold text-gray-900 leading-tight">{title}</h2>
            {subtitle && <div className="text-sm text-gray-500 mt-0.5">{subtitle}</div>}
            {showHint && (
              <p className="text-xs text-slate-600 mt-1.5 font-medium">Click the header to expand and load this section</p>
            )}
          </div>
          <div
            className="flex items-center gap-1.5 shrink-0"
            aria-hidden
          >
            {showHint && (
              <span className="hidden sm:inline text-xs font-semibold uppercase tracking-wide text-pink-700/90">Expand</span>
            )}
            <span
              className={`inline-flex h-8 w-8 items-center justify-center rounded-md border ${
                showHint
                  ? 'border-pink-200 bg-pink-50 text-pink-700 group-hover:border-pink-300 group-hover:bg-pink-100/80'
                  : 'border-transparent text-gray-500 group-hover:border-gray-200 group-hover:bg-white'
              }`}
            >
              <span className="text-sm select-none" aria-hidden>
                {open ? '▼' : '▶'}
              </span>
            </span>
          </div>
        </button>
        {headerEnd && <div className="pt-2 pr-2 shrink-0 flex items-center">{headerEnd}</div>}
      </div>
      {open && (
        <div
          className="border-t border-gray-100 p-4"
          id={`${id}-panel`}
          role="region"
          aria-labelledby={`${id}-header`}
        >
          {children}
        </div>
      )}
    </div>
  );
};

export default CollapsibleReportSection;
